import { BridgeUnavailableError, NfcWriteError, toErrorCode, type NfcPayload, type NfcWriter } from './types'

/** Thin typed wrapper over the Capacitor plugin `MotoresNfc` (implemented by the Android shell). */
export interface MotoresNfcPlugin {
  getStatus(): Promise<{ supported: boolean; enabled: boolean }>
  writeProtected(args: { url: string; passwordHex: string; packHex: string; auth0: number }): Promise<{ uid: string; alreadyProtected: boolean }>
}

interface CapacitorLike {
  isNativePlatform?: () => boolean
  Plugins?: { MotoresNfc?: MotoresNfcPlugin }
  registerPlugin?: (name: string) => unknown
}

/** Capacitor global injected by the Android shell; absent in a regular browser. */
export function capacitor(): CapacitorLike | undefined {
  return (window as unknown as { Capacitor?: CapacitorLike }).Capacitor
}

function plugin(): MotoresNfcPlugin {
  const cap = capacitor()
  const found = cap?.Plugins?.MotoresNfc ?? cap?.registerPlugin?.('MotoresNfc')
  if (!found) throw new BridgeUnavailableError('MotoresNfc plugin missing')
  return found as MotoresNfcPlugin
}

export function createAndroidWriter(): NfcWriter {
  return {
    kind: 'android',

    async getStatus() {
      const status = await plugin().getStatus()
      if (!status.supported) return { state: 'no_nfc' }
      if (!status.enabled) return { state: 'nfc_disabled' }
      return { state: 'ready' }
    },

    async write(payload: NfcPayload) {
      try {
        const result = await plugin().writeProtected({ url: payload.url, passwordHex: payload.password_hex, packHex: payload.pack_hex, auth0: payload.auth0 })
        return { uid: result.uid, alreadyProtected: result.alreadyProtected === true }
      } catch (error) {
        if (error instanceof NfcWriteError || error instanceof BridgeUnavailableError) throw error
        const e = error as { code?: unknown; message?: unknown } | null
        throw new NfcWriteError(toErrorCode(e?.code), typeof e?.message === 'string' ? e.message : undefined)
      }
    },
  }
}
